import React, { Component, ErrorInfo, ReactNode } from "react";
import { showAppError } from "../utils/showAppError";
import ErrorAlertModal from "./ErrorAlertModal";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorKey: number;
}

/**
 * Catches React render errors → ErrorAlertModal (Redux), no fullscreen debug page.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorKey: 0,
    };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("React Error Boundary caught:", error, errorInfo);
    showAppError({
      title: "Ошибка приложения",
      message: error?.message || "Something went wrong",
      details: [
        error?.stack,
        errorInfo?.componentStack
          ? `Component stack:${errorInfo.componentStack}`
          : "",
      ]
        .filter(Boolean)
        .join("\n\n"),
    });
  }

  private recover = () => {
    this.setState((s) => ({
      hasError: false,
      errorKey: s.errorKey + 1,
    }));
  };

  render() {
    return (
      <>
        {this.state.hasError ? (
          <div className="grid min-h-[100dvh] place-content-center gap-4 bg-primary p-6 text-center">
            <p className="text-sm text-darkText/80">
              Произошла ошибка. Подробности — в окне уведомления.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={this.recover}
                className="rounded-lg border border-borderColor px-4 py-2 text-sm text-darkText transition hover:bg-dropdownOptionHover"
              >
                Попробовать снова
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-lg bg-themeColor px-4 py-2 text-sm font-medium text-themeTextColor"
              >
                Обновить страницу
              </button>
            </div>
          </div>
        ) : (
          <React.Fragment key={this.state.errorKey}>
            {this.props.children}
          </React.Fragment>
        )}
        {/* Always mounted so showAppError works even when tree is replaced */}
        <ErrorAlertModal />
      </>
    );
  }
}
